import { useState } from 'react'
import { Link, useLocation } from 'react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ShieldAlert, CheckCircle, FileText } from 'lucide-react'
import apiClient from '../../api/client'
import { getApiErrorMessage } from '../../api/errors'
import { usePermissions } from '../../hooks/usePermissions'
import { Button, Alert } from '../ui'
import { useBodyScrollLock } from '../ui/useBodyScrollLock'
import type { DpaStatusDto } from '../../api/generated/dtos'

// Banner/modale di accettazione della Nomina a Responsabile del trattamento
// ex art. 28 GDPR (Allegato A delle Condizioni).
// Solo Manager/Admin (Permissions.Agency.Settings) possono accettare; gli altri
// vedono un avviso non bloccante. L'import massivo e gli invii automatici
// partono solo dopo l'accettazione della versione corrente.
// La versione tecnica (es. v1.0) resta solo in API/DB, mai mostrata in UI.
export function DpaBanner() {
  const queryClient = useQueryClient()
  const location = useLocation()
  const { can } = usePermissions()
  const canAccept = can('Permissions.Agency.Settings')
  const [checked, setChecked] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const { data: status } = useQuery<DpaStatusDto>({
    queryKey: ['agency', 'dpa-status'],
    queryFn: () => apiClient.get('/agency/dpa-status').then((r) => r.data),
    staleTime: 5 * 60 * 1000,
    retry: false,
  })
  useBodyScrollLock(!!status && !status.isAccepted && location.pathname !== '/legal/nomina-trattamento-dati' && canAccept)

  const acceptMutation = useMutation({
    mutationFn: () =>
      apiClient.post('/agency/dpa-accept', { version: status?.currentVersion }).then((r) => r.data),
    onSuccess: () => {
      setError(null)
      setChecked(false)
      queryClient.invalidateQueries({ queryKey: ['agency', 'dpa-status'] })
      queryClient.invalidateQueries({ queryKey: ['agency', 'current'] })
    },
    onError: (err) => setError(getApiErrorMessage(err, "Errore durante l'accettazione. Riprova.")),
  })

  if (!status || status.isAccepted) return null

  // Sulla pagina del testo legale non mostrare la modale (coprirebbe il testo).
  if (location.pathname === '/legal/nomina-trattamento-dati') return null

  if (!canAccept) {
    return (
      <div className="container-fluid pt-3">
        <Alert color="warning">
          <span className="flex items-center gap-2">
            <ShieldAlert size={16} />
            La nomina a Responsabile del trattamento dati (art. 28 GDPR) è in attesa di accettazione
            da parte di un amministratore dell'agenzia. Alcune funzioni (import massivi) restano sospese.
          </span>
        </Alert>
      </div>
    )
  }

  // Modale intenzionalmente non chiudibile (niente X, niente overlay-click, niente
  // Escape): l'accettazione è obbligatoria per usare i dati reali.
  return (
    <div className="fixed inset-0 z-modal flex items-center justify-center overscroll-none p-4">
      <div className="absolute inset-0 bg-overlay/50" />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Nomina a Responsabile del trattamento dati"
        className="relative z-10 flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden overscroll-contain rounded-lg bg-canvas shadow-xl"
      >
        <div className="shrink-0 border-b border-border px-4 py-3">
          <h5 className="m-0 text-base font-semibold">Nomina a Responsabile del trattamento dati</h5>
        </div>
        <div className="min-h-0 overflow-y-auto overscroll-contain p-4">
          <p className="text-sm">
            Per usare DomusAurea con dati di clienti reali è necessario accettare la{' '}
            <strong>Nomina a Responsabile del trattamento ex art. 28 GDPR</strong> - Allegato A delle
            Condizioni.
          </p>
          <p className="text-sm text-muted">
            La nomina disciplina import/migrazione, hosting (UE), sub-responsabili e sicurezza dei dati
            della tua agenzia. Finché non viene accettata, gli import massivi restano sospesi.
          </p>
          <p className="text-sm">
            <Link
              to="/legal/nomina-trattamento-dati"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5"
            >
              <FileText size={15} /> Leggi il testo completo della nomina
            </Link>
          </p>
          {error && (
            <Alert color="danger" className="my-2">
              {error}
            </Alert>
          )}
          <div className="mt-3 flex items-start gap-2">
            <input
              className="form-check-input mt-1 shrink-0"
              type="checkbox"
              id="dpa-accept-check"
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
            />
            <label className="form-check-label text-sm" htmlFor="dpa-accept-check">
              Ho letto e accetto la nomina a Responsabile del trattamento in nome e per conto
              dell'agenzia
            </label>
          </div>
        </div>
        <div className="flex shrink-0 justify-end gap-2 border-t border-border px-4 py-3">
          <Button
            variant="primary"
            icon={<CheckCircle size={16} />}
            disabled={!checked || acceptMutation.isPending}
            onClick={() => acceptMutation.mutate()}
          >
            {acceptMutation.isPending ? 'Registrazione...' : 'Accetto la nomina'}
          </Button>
        </div>
      </div>
    </div>
  )
}
